import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap, useMapEvents } from 'react-leaflet';
import Control from 'react-leaflet-custom-control';
import { LatLngTuple, Point, latLngBounds } from 'leaflet';
import { Button, Tooltip } from '@mui/material';
import { ctxPluto } from '@/contexts/PlutoContext';
import { Target } from '@/tmp';
import { AspectRatio } from '@mui/icons-material';
import { Dispatch } from 'react';
import { SetStateAction } from 'react';
import { isValidLatLong } from '@/utilities/latlong';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

export function TrackingBoundaryComponent({
  fullTargets,
  selectedTargetId,
  setSelectedTargetId,
}: {
  fullTargets: Target[] | undefined;
  selectedTargetId: number | undefined;
  setSelectedTargetId: Dispatch<SetStateAction<number | undefined>>;
}) {
  const { t } = useTranslation();
  const ctx = useContext(ctxPluto);
  const [autoAdjust, setAutoAdjust] = useState<boolean>(true);
  const map = useMap();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  function getPositionsFromTargets(fullTargets: Target[] | undefined) {
    if (selectedTargetId && fullTargets) {
      const selectedTargets = fullTargets?.filter((fullTarget) => fullTarget.target.id == selectedTargetId);
      if (selectedTargets && selectedTargets.length > 0) {
        const currentPosition = selectedTargets[0].currentPosition;
        if (!currentPosition) return undefined;
        return [[currentPosition.latitude, currentPosition.longitude] as LatLngTuple];
      }
    }
    if (fullTargets) {
      return fullTargets
        .filter((t) => isValidLatLong(t.currentPosition))
        .map((t) => [t.currentPosition!.latitude, t.currentPosition!.longitude] as LatLngTuple);
    }
  }

  useMapEvents({
    resize: () => {
      if (autoAdjust) setBoundaries(true);
    },
    dragstart: () => {
      if (!fullTargets) {
        return;
      }
      setAutoAdjust(false);
      setSelectedTargetId(undefined);
    },
    zoomend: () => {
      if (!fullTargets) {
        return;
      }
      const positions = getPositionsFromTargets(fullTargets);
      if (!positions || !positions.length) {
        return;
      }
      const mapBounds = map.getBounds();
      const positionsBounds = latLngBounds(positions);
      if (!mapBounds.contains(positionsBounds)) {
        setAutoAdjust(false);
        setSelectedTargetId(undefined);
        return;
      }
    },
  });

  useEffect(() => {
    if (selectedTargetId) {
      setAutoAdjust(true);
    }
  }, [selectedTargetId]);

  useEffect(() => {
    if (!map || !autoAdjust) return;
    // debounce
    let timer: NodeJS.Timeout | undefined = undefined;
    resizeObserverRef.current = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          map.invalidateSize();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
          // ezzel nem tudunk mit csinalni...
        }
      }, 100);
    });
    resizeObserverRef.current.observe(map.getContainer());

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [map, autoAdjust]);

  const setBoundaries = useCallback(
    (useMapZoomLevel: boolean) => {
      if (!fullTargets) {
        return;
      }
      try {
        const positions = getPositionsFromTargets(fullTargets);
        if (!positions || !positions.length) {
          return;
        }
        const bounds = latLngBounds(positions);
        const boundPadding = new Point(25, 50);
        if (useMapZoomLevel) {
          const zoom = map.getZoom();
          map.fitBounds(bounds, {
            padding: boundPadding,
            animate: false,
            maxZoom: zoom,
          });
        } else {
          map.fitBounds(bounds, {
            padding: boundPadding,
            animate: false,
            maxZoom: 18,
          });
        }
      } catch (e) {
        console.log(e);
      }
    },
    [fullTargets, selectedTargetId, map]
  );

  useEffect(() => {
    if (!ctx) return;
    if (ctx.focusedTarget === null) {
      setAutoAdjust(true);
      setTimeout(() => ctx.setFocusedTarget(undefined), 10);
      setBoundaries(true);
      return;
    }
    if (ctx.focusedTarget && ctx.focusedTarget.currentPosition && isValidLatLong(ctx.focusedTarget.currentPosition)) {
      map.setView([ctx.focusedTarget.currentPosition.latitude, ctx.focusedTarget.currentPosition.longitude], 18);
      setSelectedTargetId(ctx.focusedTarget?.target?.id);
      setAutoAdjust(true);
      setTimeout(() => ctx.setFocusedTarget(undefined), 10); // ettol szar
      setBoundaries(true);
      return;
    }
    if (autoAdjust) {
      setBoundaries(true);
    }
  }, [ctx, fullTargets, setBoundaries]);

  useEffect(() => {
    if (autoAdjust) {
      setBoundaries(false);
    }
  }, [fullTargets?.length]);

  useEffect(() => {
    if (autoAdjust) {
      setBoundaries(false);
    }
  }, [autoAdjust]);

  function fitMapOnClick() {
    if (selectedTargetId) {
      // this case autoAdjust true, just have to unselect the target
      setSelectedTargetId(undefined);
    } else {
      setAutoAdjust((prev) => !prev);
    }
  }

  //hozzáadja a leaflet zoom controlhoz az automata teszt id-t
  useEffect(() => {
    if (!map) return;
    const zoomInBtn = document.querySelector('.leaflet-control-zoom-in') as HTMLElement | null;
    const zoomOutBtn = document.querySelector('.leaflet-control-zoom-out') as HTMLElement | null;

    if (zoomInBtn) {
      zoomInBtn.setAttribute('data-qa-element-id', 'map_zoom_in_button');
    }
    if (zoomOutBtn) {
      zoomOutBtn.setAttribute('data-qa-element-id', 'map_zoom_out_button');
    }
  }, [map]);

  return (
    <Control prepend position="bottomright">
      <Tooltip placement={isPhoneOrHighScreen() ? 'left' : 'bottom'} title={t('map.controllButtons.fitMap')}>
        <Button
          onClick={fitMapOnClick}
          style={{
            backgroundColor: autoAdjust ? 'var(--mui-palette-secondary-main)' : 'white',
            color: autoAdjust ? 'white' : 'var(--mui-palette-text-primary)',
          }}
          data-qa-element-id="align_map_button"
        >
          <AspectRatio />
        </Button>
      </Tooltip>
    </Control>
  );
}
